import { IconTrash, IconPlusRounded, IconChevronLeft } from "@aksara-ui/icons";
import {
  Badge,
  Box,
  Button,
  Card,
  IconButton,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  UnstyledButton,
  theme,
} from "@aksara-ui/react";
import React, { useState, useEffect } from "react";
import TierRewardAddModal from "./TierRewardAddModal";
import { useDispatch, useSelector } from "react-redux";
import { getTierRewardByTierName } from "redux/actions";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import moment from "moment";
import TierRewardConfirmationModal from "./TierRewardConfirmationModal";
import { Link } from "react-router-dom";
import { toCapitilizeCase } from "utilities/helper/toCapitalizeCase";
import { useMediaQuery } from "react-responsive";
import { LazyLoadImage } from "react-lazy-load-image-component";

interface Props {
  tierName?: string | undefined;
  type?: string;
}

const TierRewardsTabsContent: React.FC<Props> = ({ tierName, type }) => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });

  const [selectedTierRewardId, setSelectedTierRewardId] = useState(0);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const { isLoading, appUrl } = useSelector(
    (state: any) => state.globalReducer
  );
  const { tierRewards, tierId } = useSelector(
    (state: any) => state.tierReducer
  );

  const handleOpenAddModal = () => {
    setOpenAddModal(true);
  };

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
  };

  const handleOpenConfirmationModal = (tierRewardId: number) => {
    setOpenConfirmationModal(true);
    setSelectedTierRewardId(tierRewardId);
  };

  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(false);
    setSelectedTierRewardId(0);
  };

  useEffect(() => {
    dispatch(getTierRewardByTierName(tierName, type) as any);
  }, [dispatch, tierName, type]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingBottom={20}
      >
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          style={{ gap: 20 }}
        >
          <Card
            style={{
              backgroundColor: "white",
              borderColor: `${theme.colors.grey04}`,
              borderWidth: 2,
              borderRadius: "50%",
            }}
            elevation={2}
          >
            <Link to={"../tiers"}>
              <UnstyledButton
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                size={35}
              >
                <IconChevronLeft size={20} />
              </UnstyledButton>
            </Link>
          </Card>
          <Text scale={[500, 600]} fontWeight={700}>
            Tier Reward
          </Text>
        </Box>

        <Button
          variant="primary"
          borderRadius={10}
          style={{ fontWeight: 600 }}
          icon={IconPlusRounded}
          iconPosition={"left"}
          size={32}
          onClick={() => handleOpenAddModal()}
        >
          Add Tier Reward
        </Button>
      </Box>
      <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
        <Table>
          <TableHead position={"sticky"} top={0} backgroundColor={"white"}>
            <TableHeadRow>
              <TableHeadCell>{type} Name</TableHeadCell>
              <TableHeadCell>Image</TableHeadCell>
              <TableHeadCell>Product</TableHeadCell>
              <TableHeadCell>Expiry Date</TableHeadCell>
              <TableHeadCell>Category</TableHeadCell>
              <TableHeadCell>Quantity</TableHeadCell>
              <TableHeadCell>Action</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={7} />
            ) : !tierRewards.length ? (
              <TableBodyRow>
                <TableBodyCell colSpan={7} textAlign={"center"}>
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              tierRewards.map((tierReward: any) => {
                return (
                  <TableBodyRow key={tierReward.catalog_id}>
                    <TableBodyCell>{tierReward.catalog_name}</TableBodyCell>
                    <TableBodyCell>
                      {tierReward.catalog_img_url ? (
                        <LazyLoadImage
                          key={tierReward.id}
                          src={`${appUrl}/storage/${tierReward.catalog_img_url}`}
                          alt="catalog-image"
                          height={"100%"}
                          width={120}
                          style={{ borderRadius: 20 }}
                          effect="blur"
                        />
                      ) : (
                        ""
                      )}
                    </TableBodyCell>
                    <TableBodyCell>{tierReward.catalog_product}</TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      <Badge size="md" variant="info">
                        {moment(tierReward.expiry_date).format("DD MMM YYYY")}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      {toCapitilizeCase(tierReward.reward_category)}
                    </TableBodyCell>
                    <TableBodyCell>
                      <Badge size="md" variant="info">
                        {tierReward.reward_quantity}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      <IconButton
                        onClick={() =>
                          handleOpenConfirmationModal(tierReward.tier_reward_id)
                        }
                      >
                        <IconTrash fill={theme.colors.red06} />
                      </IconButton>
                    </TableBodyCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <TierRewardAddModal
        tierId={tierId}
        tierName={tierName}
        type={type}
        isOpen={openAddModal}
        onClose={() => handleCloseAddModal()}
      />

      <TierRewardConfirmationModal
        tierRewardId={selectedTierRewardId}
        tierId={tierId}
        tierName={tierName}
        type={type}
        isOpen={openConfirmationModal}
        onClose={() => handleCloseConfirmationModal()}
      />
    </Box>
  );
};

export default TierRewardsTabsContent;
